import { DateTime } from 'luxon';

function displayInstantDateTime(value: string | Date | null | undefined): DateTime | null {
  if (value == null || value === '') return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : DateTime.fromJSDate(value, { zone: 'utc' });
  }
  const date = parseAdminTimestamp(value);
  if (!date) return null;
  return DateTime.fromJSDate(date, { zone: 'utc' });
}

export function formatRelativeTime(value: string | Date | null | undefined): string | null {
  const dt = displayInstantDateTime(value);
  if (!dt?.isValid) return null;
  return dt.toRelative() ?? null;
}

export type FormatAbsoluteTimeOptions = {
  timeZone?: string;
  dateStyle?: 'medium' | 'short' | 'long';
  timeStyle?: 'short' | 'medium';
  hour12?: boolean;
};

/** Absolute wall time in the given IANA zone (default UTC). */
export function formatAbsoluteTime(
  value: string | Date | null | undefined,
  options: FormatAbsoluteTimeOptions = {},
): string {
  if (value == null || value === '') return '—';
  const date = value instanceof Date ? value : parseAdminTimestamp(value);
  if (!date || Number.isNaN(date.getTime())) return '—';
  const timeZone = options.timeZone || 'UTC';
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: options.timeStyle === 'medium' ? '2-digit' : undefined,
      hour12: options.hour12 ?? false,
      timeZoneName: 'short',
    }).format(date);
  } catch {
    return date.toISOString();
  }
}

export function formatDateTimeWithRelative(
  value: string | Date | null | undefined,
  options: FormatAbsoluteTimeOptions = {},
): string {
  const absolute = formatAbsoluteTime(value, options);
  const relative = formatRelativeTime(value);
  if (!relative || absolute === '—') return absolute;
  return `${absolute} (${relative})`;
}

/** Legacy logs table shape (24h, no seconds). */
export function formatLogDateTime(
  value: string | null | undefined,
  timeZone?: string,
): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  if (timeZone) {
    return formatAbsoluteTime(value, { timeZone, hour12: false });
  }
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).format(date);
}

export function formatLogDateTimeTitle(
  value: string | null | undefined,
  timeZone?: string,
): string {
  const absolute = formatLogDateTime(value, timeZone);
  const relative = formatRelativeTime(value);
  if (!relative || absolute === '—') return absolute;
  return `${absolute} (${relative})`;
}

/** Normalize DB/API timestamps (ISO, MySQL UTC datetime, or epoch ms) to a Date. */
export function parseAdminTimestamp(
  value: string | Date | number | null | undefined,
): Date | null {
  if (value == null || value === '') return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return null;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  const raw = String(value).trim();
  if (!raw) return null;
  if (/^\d+$/.test(raw)) {
    const n = Number(raw);
    if (!Number.isFinite(n)) return null;
    const ms = raw.length <= 10 ? n * 1000 : n;
    const date = new Date(ms);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  // API/DB UTC instants often omit "Z"; ECMAScript treats those as browser-local otherwise.
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?$/.test(raw)) {
    const date = new Date(`${raw}Z`);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  const normalized = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(raw)
    ? `${raw.replace(' ', 'T')}Z`
    : raw;
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? null : date;
}

export type FormatAdminDateTimeLocalOptions = {
  /** Shown when value is null/empty/invalid. Default: "—" */
  empty?: string;
};

function formatDateTimeLocal24WithAbbrFromDate(date: Date): string {
  const parts = new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    timeZoneName: 'short',
  }).formatToParts(date);
  let main = '';
  let tz = '';
  for (const p of parts) {
    if (p.type === 'timeZoneName') tz = p.value;
    else main += p.value;
  }
  return `${main.trim()} ${tz}`.trim();
}

/**
 * Canonical application-user datetime: browser-local wall time, 24h, short TZ abbr.
 * Not for report recipient / site-operational incident time (use reports-admin-format).
 */
export function formatAdminDateTimeLocal(
  value: string | Date | number | null | undefined,
  options: FormatAdminDateTimeLocalOptions = {},
): string {
  const empty = options.empty ?? '—';
  const date = parseAdminTimestamp(value);
  if (!date) return empty;
  return formatDateTimeLocal24WithAbbrFromDate(date);
}

function adminDateTimeParts(date: Date): { date: string; time: string } {
  const parts = new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    timeZoneName: 'short',
  }).formatToParts(date);
  const byType: Record<string, string> = {};
  for (const p of parts) {
    if (p.type !== 'literal') byType[p.type] = p.value;
  }
  const dateLine = [byType.year, byType.month, byType.day].every(Boolean)
    ? `${byType.month}/${byType.day}/${byType.year}`
    : formatDateTimeLocal24WithAbbrFromDate(date);
  const timeLine = [byType.hour, byType.minute, byType.second].every(Boolean)
    ? `${byType.hour}:${byType.minute}:${byType.second}${byType.timeZoneName ? ` ${byType.timeZoneName}` : ''}`
    : '';
  return { date: dateLine, time: timeLine };
}

/** Browser-local calendar date (MM/DD/YYYY). */
export function formatAdminDateLocal(
  value: string | Date | number | null | undefined,
  options: FormatAdminDateTimeLocalOptions = {},
): string {
  const empty = options.empty ?? '—';
  const date = parseAdminTimestamp(value);
  if (!date) return empty;
  return adminDateTimeParts(date).date;
}

/** Browser-local 24h time with short TZ abbr. */
export function formatAdminTimeLocal(
  value: string | Date | number | null | undefined,
  options: FormatAdminDateTimeLocalOptions = {},
): string {
  const empty = options.empty ?? '—';
  const date = parseAdminTimestamp(value);
  if (!date) return empty;
  return adminDateTimeParts(date).time || empty;
}

/** @deprecated Prefer formatAdminDateTimeLocal — kept for existing imports. */
export function formatDateTimeLocal24WithAbbr(dateOrString: string | Date | null | undefined): string {
  return formatAdminDateTimeLocal(dateOrString);
}

/** Absolute local time plus relative when available (tooltips, status hints). */
export function formatAdminDateTimeLocalWithRelative(
  value: string | Date | number | null | undefined,
  options: FormatAdminDateTimeLocalOptions = {},
): string {
  const empty = options.empty ?? '—';
  const date = parseAdminTimestamp(value);
  if (!date) return empty;
  const absolute = formatDateTimeLocal24WithAbbrFromDate(date);
  const relative = formatRelativeTime(date);
  if (!relative) return absolute;
  return `${absolute} (${relative})`;
}
